"use client";

import { useRef } from "react";
import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { LandingSectionBg } from "@/components/landing/landing-section-bg";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { ResearchIcon } from "@/components/icons/research-icon";
import { LANDING_SECTION_MAX } from "@/lib/landing/layout";
import { landingBody, landingH2 } from "@/lib/landing/brandbook-ui";
import {
  DEFAULT_PARTNERS,
  type LandingPartnerItem,
} from "@/lib/landing/puck/partners-defaults";
import {
  landingTextProps,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingPartnersViewProps = {
  sectionId?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  backgroundImageUrl?: string;
  backgroundColor?: string;
  layout?: "marquee" | "row";
  items?: LandingPartnerItem[];
};

function partnerLogo(item: LandingPartnerItem): string {
  return typeof item.logoUrl === "string" ? item.logoUrl.trim() : "";
}

function partnerHref(href: unknown): string {
  if (typeof href !== "string") return "";
  return href.trim();
}

function PartnerLogo({
  item,
  index,
}: {
  item: LandingPartnerItem;
  index: number;
}) {
  const logo = partnerLogo(item);
  const href = partnerHref(item.href);
  const title = styledTextString(item.title) || "Партнёр";

  const inner = (
    <span className="flex h-[4.5rem] w-[10.5rem] shrink-0 items-center justify-center px-3 sm:h-[5rem] sm:w-[12rem]">
      {logo ? (
        // eslint-disable-next-line @next/next/no-img-element -- CMS / S3
        <img
          src={logo}
          alt={title}
          className="max-h-full max-w-full object-contain opacity-80 grayscale transition-[opacity,filter] duration-300 group-hover:opacity-100 group-hover:grayscale-0"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-md border border-dashed border-border px-2 text-center text-xs text-[var(--color-text-secondary)]">
          <LandingCanvasText
            field={`items.${index}.title`}
            value={item.title}
            label="Название"
            hideIfEmpty
          >
            <span {...landingTextProps(item.title)} />
          </LandingCanvasText>
        </span>
      )}
    </span>
  );

  if (!href) {
    return (
      <div className="group inline-flex" title={title}>
        {inner}
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group inline-flex no-underline"
      title={`${title} — откроется в новой вкладке`}
      aria-label={`${title} — сайт в новой вкладке`}
    >
      {inner}
    </a>
  );
}

function PartnersMarquee({ items }: { items: LandingPartnerItem[] }) {
  /** Дублируем ряд для бесшовной ленты. */
  const track = [...items, ...items];

  return (
    <div className="landing-partners-marquee relative w-full overflow-hidden py-1">
      <div className="landing-partners-marquee__track flex w-max items-center gap-10 sm:gap-14">
        {track.map((item, index) => (
          <PartnerLogo
            key={`m-${index}-${styledTextString(item.title)}`}
            item={item}
            index={index % items.length}
          />
        ))}
      </div>
    </div>
  );
}

function PartnersRow({ items }: { items: LandingPartnerItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.6), behavior: "smooth" });
  };

  const showNav = items.length > 3;

  return (
    <div className="relative w-full">
      {showNav ? (
        <div className="mb-4 flex justify-end gap-2">
          <button
            type="button"
            aria-label="Предыдущие партнёры"
            onClick={() => scrollBy(-1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-[var(--color-background-body)] text-[var(--color-text-primary)] transition-colors hover:border-[var(--landing-accent,#E11D48)] hover:text-[var(--landing-accent,#E11D48)]"
          >
            <ResearchIcon name="arrowLeft" size="sm" />
          </button>
          <button
            type="button"
            aria-label="Следующие партнёры"
            onClick={() => scrollBy(1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-[var(--color-background-body)] text-[var(--color-text-primary)] transition-colors hover:border-[var(--landing-accent,#E11D48)] hover:text-[var(--landing-accent,#E11D48)]"
          >
            <ResearchIcon name="arrowRight" size="sm" />
          </button>
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className="flex w-full snap-x snap-mandatory gap-8 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-10 [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, index) => (
          <div key={`r-${index}-${styledTextString(item.title)}`} className="snap-start">
            <PartnerLogo item={item} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Лента логотипов партнёров (marquee / ряд). Не новости — только лого + сайт в новой вкладке. */
export function LandingPartnersView({
  sectionId = DEFAULT_PARTNERS.sectionId,
  title = DEFAULT_PARTNERS.title,
  subtitle = DEFAULT_PARTNERS.subtitle,
  backgroundImageUrl = DEFAULT_PARTNERS.backgroundImageUrl,
  backgroundColor = DEFAULT_PARTNERS.backgroundColor,
  layout = DEFAULT_PARTNERS.layout,
  items = DEFAULT_PARTNERS.items,
}: LandingPartnersViewProps) {
  const list =
    Array.isArray(items) && items.length > 0 ? items : DEFAULT_PARTNERS.items;
  const mode = layout === "row" ? "row" : "marquee";

  return (
    <LandingSectionBg
      backgroundImageUrl={backgroundImageUrl}
      backgroundColor={backgroundColor}
    >
      <Section id={sectionId || undefined} variant="transparent" paddingBlock={10}>
        <Center>
          <div
            className="mx-auto flex w-full min-w-0 flex-col gap-8 px-6"
            style={{ maxWidth: LANDING_SECTION_MAX }}
          >
            <VStack gap={3} align="start" width="100%" className="min-w-0 max-w-[46rem]">
              <LandingCanvasText field="title" value={title} label="Заголовок" multiline>
                <Heading
                  level={2}
                  className={`${landingH2} break-words text-balance`}
                  {...landingTextProps(title)}
                />
              </LandingCanvasText>
              <LandingCanvasText
                field="subtitle"
                value={subtitle}
                label="Подзаголовок"
                multiline
                hideIfEmpty
              >
                <Text
                  className={`${landingBody} break-words text-pretty`}
                  {...landingTextProps(subtitle)}
                />
              </LandingCanvasText>
            </VStack>

            {mode === "marquee" ? (
              <PartnersMarquee items={list} />
            ) : (
              <PartnersRow items={list} />
            )}
          </div>
        </Center>
      </Section>
    </LandingSectionBg>
  );
}
